import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { TableActions } from "../../../components/TableActions";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { Edit, Trash2, FolderTree, Boxes } from "lucide-react";
import { CategoryDialog } from "./CategoryDialog";
import { CategoryProductsDialog } from "./CategoryProductsDialog";
import { toast } from "react-toastify";
import { Category, CategoryPayload } from "../../../types/category";
import { useCategoryStore } from "../../../store/categoryStore"; // Bu store'u oluşturmalısın
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types";

export const CategoryList = () => {
  const store = useCategoryStore();
  const { createItem, updateItem, deleteItem, data } = store;
  const { handleApiError } = useApiErrorHandler();
  const { confirm, modal: confirmModal } = useConfirmModal();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<Category | null>(null);

  // Kategori Ürünleri Modalı State'leri
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [selectedCategoryForProducts, setSelectedCategoryForProducts] = useState<Category | null>(null);

  const handleSave = async (data: CategoryPayload) => {
    try {
      if (editItem) {
        await updateItem(editItem.id, data);
        toast.success("Kategori güncellendi");
      } else {
        await createItem(data);
        toast.success("Kategori eklendi");
      }
      setIsModalOpen(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDelete = async (cat: Category) => {
    const ok = await confirm(`${cat.name} kategorisini silmek istediğine emin misin?`);
    if (ok) {
      try {
        await deleteItem(cat.id);
        toast.success("Kategori silindi");
      } catch (error) {
        handleApiError(error);
      }
    }
  };

  const handleViewProducts = (cat: Category) => {
    setSelectedCategoryForProducts(cat);
    setIsProductsModalOpen(true);
  };

  const actionBody = (rowData: Category) => (
    <TableActions
      actions={[
        {
          icon: Boxes,
          colorClass: "text-emerald-600 dark:text-emerald-400 hover:text-emerald-700",
          title: "Ürünleri Görüntüle",
          onClick: () => handleViewProducts(rowData)
        },
        {
          icon: Edit,
          colorClass: "text-blue-600 hover:text-blue-700",
          title: "Düzenle",
          onClick: () => { setEditItem(rowData); setIsModalOpen(true); }
        },
        {
          icon: Trash2,
          colorClass: "text-red-500 hover:text-red-700",
          title: "Sil",
          onClick: () => handleDelete(rowData)
        }
      ]}
    />
  );

  const parentBody = (rowData: Category) => (
    <span className="text-gray-600 dark:text-gray-300">
      {rowData.parent ? rowData.parent.name : <small className="text-gray-400">Ana Kategori</small>}
    </span>
  );

  const productCountBody = (rowData: Category) => {
    const count = rowData._count?.stocks ?? 0;
    return (
      <button
        onClick={() => handleViewProducts(rowData)}
        className="font-bold text-xs px-2.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition cursor-pointer"
        title="Kategoriye ait ürünleri görüntülemek için tıklayın"
      >
        {count} Ürün
      </button>
    );
  };

  const columns: DataTableColumn<Category>[] = [
    { field: "name", header: "Kategori Adı", sortable: true, width: "120px" },
    { field: "parentId", header: "Üst Kategori", body: parentBody, width: "100px" },
    { field: "description", header: "Açıklama", width: "120px" },
    { field: "_count.stocks", header: "Ürün Sayısı", body: productCountBody, width: "90px", align: "center" },
    { field: "actions", header: "İşlemler", body: actionBody, width: "90px", align: "right" },
  ];

  return (
    <div className="h-full flex flex-col">
      {confirmModal}
      <EDataTable<Category>
        tableKey="category_list"
        useStore={() => store}
        dataKey="id"
        title="Kategori Yönetimi"
        placeholder="Kategori Ara..."
        searchFields={[{ field: "name", operator: "contains" }]}
        actions={
          <TextButton onClick={() => { setEditItem(null); setIsModalOpen(true); }} className="btn-new">
            <FolderTree className="w-4 h-4 mr-2" /> Yeni Kategori
          </TextButton>
        }
        columns={columns}
      />
      <CategoryDialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        category={editItem}
        categories={data} // Tüm kategoriler parent seçimi için
        onSave={handleSave}
      />
      <CategoryProductsDialog
        isOpen={isProductsModalOpen}
        onClose={() => {
          setIsProductsModalOpen(false);
          setSelectedCategoryForProducts(null);
        }}
        category={selectedCategoryForProducts}
      />
    </div>
  );
};